import { useCallback, useContext } from 'react';
import firebase from 'firebase/compat/app';
import accountPickerContext from '../../../../../contexts/AccountPickerContext';
import { useGetMetric } from '../../../../GoalShow/useGoalData';
import metricTypeCheckers from '../../../../../types/metricTypeCheckers';
import currentUserContext from '../../../../../contexts/CurrentUserContext';
import addArchiveUnusedMetricTimelineEvent from './addArchiveUnusedMetricTimelineEvent';
import getTimeStamp from '../../../../../getTimeStamp';
import STORE from '../../../../../store';
import invalidateMetricCache from '../../../../../api/metrics/invalidateMetricCache';

const useArchiveUnusedMetrics = () => {
  const { selectedAccountId } = useContext(accountPickerContext);
  const { id: currentUserId, displayName: currentUserDisplayName } =
    useContext(currentUserContext);
  const getMetric = useGetMetric();

  const archiveUnusedMetrics = useCallback(
    async (metricIds: string[]) => {
      const db = firebase.firestore();
      const batch = db.batch();

      metricIds.forEach((metricId) => {
        const metric = getMetric(metricId);

        if (
          !metric ||
          (!metricTypeCheckers.isNormalMetric(metric) &&
            !metricTypeCheckers.isCompoundMetric(metric))
        )
          return;

        // Write a timeline event for each affected metric
        addArchiveUnusedMetricTimelineEvent({
          accountId: selectedAccountId,
          batch,
          currentUserName: currentUserDisplayName,
          metric,
          currentUserId,
        });

        let metricRef: firebase.firestore.DocumentReference<
          Metrics.CompoundMetric | Metrics.NormalMetric
        >;
        if (metricTypeCheckers.isCompoundMetric(metric)) {
          metricRef = STORE.metricDefinitions
            .getCompoundMetricsRef({ accountId: selectedAccountId })
            .doc(metric.id);
        } else if (metricTypeCheckers.isNormalMetric(metric)) {
          metricRef = STORE.metricDefinitions
            .getNormalMetricsRef({ accountId: selectedAccountId })
            .doc(metric.id);
        } else {
          const e = new Error('Invalid metric type');
          e.name = 'ArchiveUnusedMetrics: Invalid metric type';
          throw e;
        }

        batch.update(metricRef, {
          status: 'archived',
          updatedBy: currentUserId,
          updatedOn: getTimeStamp(),
        });
      });

      await batch.commit();

      const normalIds = metricIds.filter((id) => {
        const m = getMetric(id);
        return m && metricTypeCheckers.isNormalMetric(m);
      });
      const compoundIds = metricIds.filter((id) => {
        const m = getMetric(id);
        return m && metricTypeCheckers.isCompoundMetric(m);
      });

      await invalidateMetricCache({
        accountId: selectedAccountId,
        normalMetricIds: normalIds.length > 0 ? normalIds : undefined,
        compoundMetricIds: compoundIds.length > 0 ? compoundIds : undefined,
      });
    },
    [currentUserDisplayName, currentUserId, getMetric, selectedAccountId],
  );

  return archiveUnusedMetrics;
};

export default useArchiveUnusedMetrics;
